// src/pages/Login.js
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import API from '../services/api';
import { Form } from 'react-bootstrap';
import logo from '../assets/logo.png';
import { motion } from 'framer-motion';
import './Login.css';

const hasAny = (perms = [], list = []) =>
  list.some((p) => Array.isArray(perms) && perms.includes(p));

const authShortcuts = [
  {
    label: 'Vendor Register',
    icon: 'storefront',
    action: '/register/vendor',
  },
  {
    label: 'Supplier Register',
    icon: 'local_shipping',
    action: '/register/supplier',
  },
  {
    label: 'Employee Register',
    icon: 'badge',
    action: '/register/employee',
  },
  {
    label: 'Contractor Register',
    icon: 'engineering',
    action: '/register/contractor',
  },
];

const getEmployeeDefaultPath = ({ permissions = [], departmentName = '', roleName = '' }) => {
  const perms = Array.isArray(permissions) ? permissions : [];
  const deptRole = `${departmentName} ${roleName}`.toLowerCase();

  const isGuard =
    perms.includes('guard:access') ||
    perms.some((p) => typeof p === 'string' && p.startsWith('security:'));
  if (isGuard) return '/employee/guard/dashboard';

  const isSales = /(sales|marketing)/.test(deptRole) || hasAny(perms, [
    'leads:view',
    'leads:edit',
    'deals:view',
    'deals:edit',
    'reports:sales:view',
    'sales:targets:manage',
  ]);
  if (isSales) return '/employee/sales/dashboard';

  const isFinance = hasAny(perms, [
    'finance:invoice:create',
    'finance:invoice:approve',
    'finance:expense:create',
    'finance:expense:approve',
    'finance:vendor-bill:upload',
    'finance:vendor-bill:approve',
    'finance:payments:update',
    'finance:payments:approve',
    'finance:payments:release',
    'finance:gst:view',
    'finance:tds:view',
    'finance:reports:view',
    'finance:audit-logs:view',
    'salary:process',
    'salary:approve',
  ]);
  if (isFinance) return '/employee/accounts';

  const isPurchase = /(purchase|procurement)/.test(deptRole) || hasAny(perms, [
    'purchase:request:create',
    'purchase:order:create',
    'vendor-master:edit',
    'purchase:rate-comparison:view',
    'material:availability:view',
  ]);
  if (isPurchase) return '/employee/purchase/dashboard';

  const isPlant = /(plant|production)/.test(deptRole) || hasAny(perms, [
    'production:plan',
    'production:report',
    'stock:consumption:view',
    'maintenance:log',
    'wastage:view',
    'plant:shift:manage',
    'plant:overtime:approve',
    'plant:utilization:view',
    'plant:breakdown:view',
    'plant:maintenance:approve',
    'plant:quality:review',
    'plant:inventory:alert',
    'plant:material-movement:approve',
    'plant:contractor-work:approve',
    'plant:safety:manage',
    'plant:compliance:manage',
    'plant:coordination:manage',
    'plant:reports:manage',
  ]);
  if (isPlant) return '/employee/plant/dashboard';

  const isHr = /hr/.test(deptRole) || hasAny(perms, [
    'employees:view',
    'leave:view',
    'performance:view',
    'hr:onboarding:manage',
    'hr:recruitment:manage',
  ]);
  if (isHr) return '/employee/hr/dashboard';

  return '/employee/dashboard';
};

const Login = () => {
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const isMobileClient =
        typeof navigator !== 'undefined' &&
        /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent || '');
      const res = await API.post('/auth/login', {
        mobile,
        password,
        clientType: isMobileClient ? 'mobile' : 'desktop',
      });
      const { token, user } = res.data;
      let permissions = user?.permissions || [];
      let departmentName = '';
      let roleName = '';

      localStorage.setItem('token', token);
      try {
        const me = await API.get('/auth/me');
        permissions = me?.data?.employeeRole?.permissions || permissions || [];
        departmentName = me?.data?.department?.name || '';
        roleName = me?.data?.employeeRole?.name || '';
        localStorage.setItem(
          'user',
          JSON.stringify({
            ...user,
            permissions,
            department: me?.data?.department || user?.department || null,
            employeeRole: me?.data?.employeeRole || user?.employeeRole || null,
          })
        );
      } catch (_e) {
        localStorage.setItem('user', JSON.stringify({ ...user, permissions }));
      }
      localStorage.setItem('role', user.role);
      localStorage.setItem('permissions', JSON.stringify(Array.isArray(permissions) ? permissions : []));

      if (user.role === 'admin') {
        navigate('/admin');
      } else if (user.role === 'vendor') {
        navigate('/vendor');
      } else if (user.role === 'supplier') {
        navigate('/supplier');
      } else if (user.role === 'contractor') {
        navigate('/contractor');
      } else if (user.role === 'government') {
        navigate('/government');
      } else if (user.role === 'employee') {
        navigate(getEmployeeDefaultPath({ permissions, departmentName, roleName }));
      } else if (user.role === 'guard') {
        navigate('/employee/guard/dashboard');
      } else {
        navigate('/');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed');
    }
  };

  return (
    <div className="login-shell">
      <header className="login-topbar">
        <div className="brand">
          <img src={logo} alt="Lotus Rice Mill Pvt. Ltd." className="brand-logo" />
          <span>Lotus Rice Mill Pvt. Ltd.</span>
        </div>
        <div className="login-topbar-actions">
          <Link className="help-btn" to="/privacy-policy">Privacy Policy</Link>
          <Link className="help-btn help-btn-danger" to="/request-account-deletion">
            Delete Request
          </Link>
        </div>
      </header>

      <main className="login-main">
        <div className="login-layout">
          <section className="login-hero">
            <div className="login-hero-overlay" />
            <div className="login-hero-content">
              <div className="login-hero-brand">
                <div className="login-hero-brand-mark">
                  <img src={logo} alt="Lotus Rice Mill Pvt. Ltd." />
                </div>
                <div>
                  <h2>Lotus Rice Mill</h2>
                  <p>Pvt. Ltd.</p>
                </div>
              </div>

              <motion.div
                initial={{ y: 14, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.45 }}
                className="login-hero-copy"
              >
                <span className="login-hero-kicker">Unified operations portal</span>
                <h1>Cultivating Quality, Delivering Excellence</h1>
                <p>
                  Your trusted space to manage procurement, workforce, suppliers and day-to-day mill
                  operations from one secure dashboard.
                </p>
              </motion.div>

              <div className="login-hero-points">
                <span>ISO 9001:2015 Certified</span>
                <span>Premium Grain Processing</span>
                <span>Distribution Ready</span>
              </div>
            </div>
          </section>

          <section className="login-card">
            <div className="login-mobile-brand">
              <span className="login-mobile-kicker">Unified operations portal</span>
              <img src={logo} alt="Lotus Rice Mill Pvt. Ltd." />
              <div className="login-mobile-brand-copy">
                <h2>Lotus Rice Mill</h2>
                <p>Secure access for procurement, workforce and supplier operations.</p>
              </div>
            </div>

            <div className="login-banner">
              <div className="login-banner-glow" />
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="login-banner-logo"
              >
                <img src={logo} alt="Lotus Rice Mill Pvt. Ltd." />
              </motion.div>
            </div>

            <div className="login-header">
              <motion.h1
                initial={{ y: -10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.4 }}
              >
                Welcome Back
              </motion.h1>
              <motion.p
                initial={{ y: -10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.35, duration: 0.4 }}
              >
                Access your corporate portal to manage your operations.
              </motion.p>
            </div>

            {error && <div className="login-alert">{error}</div>}

            <Form onSubmit={handleSubmit} className="login-form">
              <div className="field">
                <label>Mobile Number</label>
                <div className="input-wrap">
                  <span className="material-symbols-outlined">phone_iphone</span>
                  <input
                    type="tel"
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value)}
                    placeholder="Enter mobile number"
                    required
                  />
                </div>
              </div>

              <div className="field">
                <div className="field-row">
                  <label>Password</label>
                  <button
                    type="button"
                    className="link-btn"
                    onClick={() => navigate('/forgot-password')}
                  >
                    Forgot password?
                  </button>
                </div>
                <div className="input-wrap">
                  <span className="material-symbols-outlined">lock</span>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    required
                  />
                  <button
                    type="button"
                    className="icon-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label="Toggle password visibility"
                  >
                    <span className="material-symbols-outlined">
                      {showPassword ? 'visibility_off' : 'visibility'}
                    </span>
                  </button>
                </div>
              </div>

              <button type="submit" className="primary-btn">
                Login
              </button>
            </Form>

            <div className="divider">
              <span>New to the portal?</span>
            </div>

            <div className="register-grid">
              {authShortcuts.map((item) => (
                <button
                  key={item.label}
                  type="button"
                  className="ghost-card"
                  onClick={() => navigate(item.action)}
                >
                  <span className="material-symbols-outlined">{item.icon}</span>
                  <span>{item.label}</span>
                </button>
              ))}
            </div>

            <div className="login-card-footer">
              <div>© 2024 Lotus Rice Mill Pvt. Ltd. All rights reserved.</div>
              <Link to="/privacy-policy" className="login-legal-link">Read Privacy Policy</Link>
            </div>
          </section>
        </div>
      </main>

      <footer className="login-footer">
        <span>© 2024 Lotus Rice Mill Pvt. Ltd. All rights reserved.</span>
        <Link to="/privacy-policy" className="login-legal-link">Privacy Policy</Link>
      </footer>
    </div>
  );
};

export default Login;
